import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getAuth } from 'firebase/auth';
import type { DashboardLayoutConfig } from '../types/dashboardLayout';
import type { StoredTradingSession } from '../config/tradingSessions';

export interface TradeFormPreferences {
  showTradingConditions: boolean;
  showTradingSetups: boolean;
  showNotes: boolean;
  showCommissionsAndSwaps: boolean;
  showTagCategories?: { [categoryId: string]: boolean };
  showPlans?: { [planId: string]: boolean };
  breakEven?: {
    enabled: boolean;
    thresholdType: 'percentage' | 'amount';
    thresholdFrom: number;
    thresholdTo: number;
  };
  customRiskPercentages?: number[];
  customTimeUnits?: Array<{
    value: string;
    label: string;
    minutes: number;
  }>;
  // Nouvelles préférences pour l'affichage des informations de trade
  showStopLoss: boolean;
  showTakeProfit: boolean;
  showEntryPrice: boolean;
  // Affichage du score et des actualités sur les cartes de trade
  showTradeScore: boolean;
  showTradeNews: boolean;
  // Template Academy pour les utilisateurs de l'académie
  useAcademyTemplate?: boolean;
  // Mode de sélection des plans : 'single' = un seul dropdown, 'detailed' = un dropdown par plan
  planSelectionMode?: 'single' | 'detailed';
}

export interface RetailPreferences {
  saveSymbolsFilter: boolean;
  saveSentimentFilter: boolean;
  showFavorites: boolean;
}

export interface SeasonalityPreferences {
  showFavorites: boolean;
  saveFilters?: boolean;
  savedFilters?: {
    savedPairs: string[];
    savedCategories: string[];
    savedTrends: string[];
    savedSearch: string;
  };
}

export interface MarketClockPreferences {
  markets: Array<{
    city: string;
    offset: number;
  }>;
}

export interface CalendarPreferences {
  saveFilters: boolean;
  selectedCountries: string[];
  selectedImpacts: string[];
  timeZoneOffset: number;
  displayMode: 'columns' | 'rows';
}

export interface IAAnalysisPreferences {
  showVersionSelector: boolean;
  analysisLength: 'concise' | 'standard' | 'detailed';
  showAIBadges: boolean;
  /** Mise en couleur vert/rouge dans les recommandations IA (comme Atlas) */
  metricHighlight?: boolean;
}

export interface AtlasPreferences {
  metricHighlight?: boolean; // Mise en couleur des métriques positives/négatives dans les réponses (défaut: true)
  whatsNewModesAccountsSeen?: boolean; // Mini-tuto "modes Global/Backtest + sélecteur de comptes" déjà vu
}

// Preset personnalisé de la calculatrice de position
export interface PositionCalculatorPreset {
  id: string;
  label: string;
  risk: string;
  sl: string;
  tp: string;
}

// Préférences pour la page Calculatrice de position
export interface PositionCalculatorPreferences {
  currencyPair: string;
  accountCurrency: string;
  riskMode: 'percentage' | 'money';
  riskValue: number;
  stopLossPips?: number;
  favoriteSymbols?: string[];
  // Flags de sauvegarde granularisés
  savePair: boolean;
  saveAccountCurrency: boolean;
  saveRisk: boolean;
  saveStopLoss?: boolean;
  // Ancien flag global (compat)
  saveEnabled?: boolean;
  // Presets personnalisés (remplace la liste par défaut si présent)
  customPresets?: PositionCalculatorPreset[];
}

export interface BlackFridayPreferences {
  datesSeen: string[];
}

export interface AffiliateOnboardingPreferences {
  showCount: number;
  lastShownDate: string | null; // ISO date string
  declinedDates: string[]; // Dates où l'utilisateur a cliqué sur "Non, merci"
}

export interface PropFirmSimulatorDisclaimerPreferences {
  acknowledged: boolean;
  acknowledgedAt: string | null; // ISO 8601 (date + heure exacte)
}

export interface UserPreferences {
  tradeForm: TradeFormPreferences;
  retail: RetailPreferences;
  seasonality: SeasonalityPreferences;
  marketClock: MarketClockPreferences;
  calendar: CalendarPreferences;
  iaAnalysis: IAAnalysisPreferences;
  atlas?: AtlasPreferences;
  positionCalculator?: PositionCalculatorPreferences;
  requireConnectionRequests?: boolean;
  blackFriday2025?: BlackFridayPreferences;
  affiliateOnboarding?: AffiliateOnboardingPreferences;
  propFirmSimulatorDisclaimer?: PropFirmSimulatorDisclaimerPreferences;
  defaultLandingPage?: string;
  globalTimezone?: string;
  /**
   * Langue choisie dans `/settings?tab=parametres`, synchronisée depuis i18next.
   * Seule autorité pour la langue de TOUTES les réponses IA du backend — y compris
   * les traitements qui n'ont pas de requête sous la main. Le sélecteur lui-même
   * reste piloté par i18next / localStorage ; ce champ n'est qu'un miroir serveur.
   */
  appLanguage?: string;
  declinedTimezoneChanges?: string[];
  showNetPnL?: boolean; // true = Net P&L (avec commissions/swaps), false = Brut P&L
  tagsLayout?: 'grid' | 'list'; // 'grid' = 2 par ligne (défaut), 'list' = 1 par ligne
  plansLayout?: 'grid' | 'list'; // 'grid' = 2 par ligne (défaut), 'list' = 1 par ligne
  tableColumns?: Array<{ key: string; visible: boolean }>; // Configuration colonnes tableau journal
  listDisplayMode?: 'cards' | 'table'; // Mode d'affichage de la liste des trades
  dashboardLayout?: DashboardLayoutConfig;
  /**
   * Sessions de trading personnalisées (carte de session du journal).
   * Absent = sessions par défaut (`DEFAULT_TRADING_SESSIONS`), donc comportement historique inchangé.
   */
  tradingSessions?: StoredTradingSession[];
}

const DEFAULT_PREFERENCES: UserPreferences = {
  tradeForm: {
    showTradingConditions: true,
    showTradingSetups: true,
    showNotes: true,
    showCommissionsAndSwaps: false,
    showTagCategories: {},
    breakEven: {
      enabled: false,
      thresholdType: 'percentage',
      thresholdFrom: 0.4,
      thresholdTo: 0.4
    },
    customRiskPercentages: [],
    customTimeUnits: [],
    // Nouvelles préférences désactivées par défaut
    showStopLoss: false,
    showTakeProfit: false,
    showEntryPrice: false,
    showTradeScore: true,
    showTradeNews: true,
    // Template Academy désactivé par défaut
    useAcademyTemplate: false,
    // Mode de sélection des plans par défaut : 'single' (un seul dropdown)
    planSelectionMode: 'single'
  },
  retail: {
    saveSymbolsFilter: false,
    saveSentimentFilter: false,
    showFavorites: true
  },
  seasonality: {
    showFavorites: true,
    saveFilters: false
  },
  marketClock: {
    markets: [
      { city: 'Tokyo', offset: 9 },
      { city: 'London', offset: 0 },
      { city: 'New York', offset: -5 }
    ]
  },
  calendar: {
    saveFilters: false,
    selectedCountries: [],
    selectedImpacts: [],
    timeZoneOffset: 2,
    displayMode: 'columns'
  },
  iaAnalysis: {
    showVersionSelector: false,
    analysisLength: 'standard',
    showAIBadges: true,
    metricHighlight: true,
  },
  atlas: {
    metricHighlight: true
  },
  positionCalculator: {
    currencyPair: 'EURUSD',
    accountCurrency: 'USD',
    riskMode: 'percentage',
    riskValue: 1,
    stopLossPips: 14,
    savePair: false,
    saveAccountCurrency: false,
    saveRisk: false,
    saveStopLoss: false
  },
  requireConnectionRequests: true,
  blackFriday2025: {
    datesSeen: []
  },
  affiliateOnboarding: {
    showCount: 0,
    lastShownDate: null,
    declinedDates: []
  },
  propFirmSimulatorDisclaimer: {
    acknowledged: false,
    acknowledgedAt: null
  },
  defaultLandingPage: '/dashboard',
  showNetPnL: true // Par défaut, afficher le P&L Net (avec commissions et swaps)
};

export const userPreferencesService = {
  async getUserPreferences(): Promise<UserPreferences> {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const preferencesDoc = await getDoc(doc(db, 'userPreferences', userId));
      
      if (!preferencesDoc.exists()) {
        // Si pas de préférences, créer les préférences par défaut
        await setDoc(doc(db, 'userPreferences', userId), DEFAULT_PREFERENCES);
        return DEFAULT_PREFERENCES;
      }

      // Migration des préférences existantes
      const currentPrefs = preferencesDoc.data() as UserPreferences;
      if (!currentPrefs.seasonality) {
        const updatedPrefs = {
          ...currentPrefs,
          seasonality: DEFAULT_PREFERENCES.seasonality
        };
        await setDoc(doc(db, 'userPreferences', userId), updatedPrefs);
        return updatedPrefs;
      }

      if (!currentPrefs.marketClock) {
        const updatedPrefs = {
          ...currentPrefs,
          marketClock: DEFAULT_PREFERENCES.marketClock
        };
        await setDoc(doc(db, 'userPreferences', userId), updatedPrefs);
        return updatedPrefs;
      }

      if (!currentPrefs.calendar) {
        const updatedPrefs = {
          ...currentPrefs,
          calendar: DEFAULT_PREFERENCES.calendar
        };
        await setDoc(doc(db, 'userPreferences', userId), updatedPrefs);
        return updatedPrefs;
      }

      if (!currentPrefs.iaAnalysis) {
        const updatedPrefs = {
          ...currentPrefs,
          iaAnalysis: DEFAULT_PREFERENCES.iaAnalysis
        };
        await setDoc(doc(db, 'userPreferences', userId), updatedPrefs);
        return updatedPrefs;
      }

      // Migration pour ajouter showAIBadges si il n'existe pas
      if (currentPrefs.iaAnalysis && currentPrefs.iaAnalysis.showAIBadges === undefined) {
        const updatedPrefs = {
          ...currentPrefs,
          iaAnalysis: {
            ...currentPrefs.iaAnalysis,
            showAIBadges: DEFAULT_PREFERENCES.iaAnalysis.showAIBadges
          }
        };
        await setDoc(doc(db, 'userPreferences', userId), updatedPrefs);
        return updatedPrefs;
      }

      if (currentPrefs.iaAnalysis && currentPrefs.iaAnalysis.metricHighlight === undefined) {
        const updatedPrefs = {
          ...currentPrefs,
          iaAnalysis: {
            ...currentPrefs.iaAnalysis,
            metricHighlight: DEFAULT_PREFERENCES.iaAnalysis.metricHighlight
          }
        };
        await setDoc(doc(db, 'userPreferences', userId), updatedPrefs);
        return updatedPrefs;
      }

      // Migration pour ajouter les préférences de la calculatrice de position
      if (!currentPrefs.positionCalculator) {
        const updatedPrefs = {
          ...currentPrefs,
          positionCalculator: DEFAULT_PREFERENCES.positionCalculator
        } as UserPreferences;
        await setDoc(doc(db, 'userPreferences', userId), updatedPrefs);
        return updatedPrefs;
      }

      // Migration pour granulariser les flags si absents
      if (currentPrefs.positionCalculator && (
        (currentPrefs.positionCalculator as any).savePair === undefined ||
        (currentPrefs.positionCalculator as any).saveAccountCurrency === undefined ||
        (currentPrefs.positionCalculator as any).saveRisk === undefined ||
        (currentPrefs.positionCalculator as any).saveStopLoss === undefined ||
        (currentPrefs.positionCalculator as any).stopLossPips === undefined
      )) {
        const pc = currentPrefs.positionCalculator as PositionCalculatorPreferences;
        const base = {
          savePair: pc.savePair ?? (pc.saveEnabled ?? true),
          saveAccountCurrency: pc.saveAccountCurrency ?? (pc.saveEnabled ?? true),
          saveRisk: pc.saveRisk ?? (pc.saveEnabled ?? true),
          saveStopLoss: pc.saveStopLoss ?? (pc.saveEnabled ?? true),
          stopLossPips: pc.stopLossPips ?? DEFAULT_PREFERENCES.positionCalculator!.stopLossPips
        };
        const updatedPrefs = {
          ...currentPrefs,
          positionCalculator: {
            ...pc,
            ...base
          }
        } as UserPreferences;
        await setDoc(doc(db, 'userPreferences', userId), updatedPrefs);
        return updatedPrefs;
      }

      // Migration pour ajouter les nouvelles préférences d'affichage des trades
      if (currentPrefs.tradeForm && (
        currentPrefs.tradeForm.showStopLoss === undefined ||
        currentPrefs.tradeForm.showTakeProfit === undefined ||
        currentPrefs.tradeForm.showEntryPrice === undefined ||
        currentPrefs.tradeForm.showTradeScore === undefined ||
        currentPrefs.tradeForm.showTradeNews === undefined
      )) {
        const updatedPrefs = {
          ...currentPrefs,
          tradeForm: {
            ...currentPrefs.tradeForm,
            showStopLoss: currentPrefs.tradeForm.showStopLoss ?? DEFAULT_PREFERENCES.tradeForm.showStopLoss,
            showTakeProfit: currentPrefs.tradeForm.showTakeProfit ?? DEFAULT_PREFERENCES.tradeForm.showTakeProfit,
            showEntryPrice: currentPrefs.tradeForm.showEntryPrice ?? DEFAULT_PREFERENCES.tradeForm.showEntryPrice,
            showTradeScore: currentPrefs.tradeForm.showTradeScore ?? DEFAULT_PREFERENCES.tradeForm.showTradeScore,
            showTradeNews: currentPrefs.tradeForm.showTradeNews ?? DEFAULT_PREFERENCES.tradeForm.showTradeNews,
          }
        };
        await setDoc(doc(db, 'userPreferences', userId), updatedPrefs);
        return updatedPrefs;
      }

      if (!currentPrefs.blackFriday2025) {
        const updatedPrefs = {
          ...currentPrefs,
          blackFriday2025: DEFAULT_PREFERENCES.blackFriday2025
        };
        await setDoc(doc(db, 'userPreferences', userId), updatedPrefs);
        return updatedPrefs;
      }

      // Migration pour ajouter affiliateOnboarding si il n'existe pas
      if (!currentPrefs.affiliateOnboarding) {
        const updatedPrefs = {
          ...currentPrefs,
          affiliateOnboarding: DEFAULT_PREFERENCES.affiliateOnboarding
        };
        await setDoc(doc(db, 'userPreferences', userId), updatedPrefs);
        return updatedPrefs;
      }

      // Migration pour ajouter defaultLandingPage si il n'existe pas
      if (currentPrefs.defaultLandingPage === undefined) {
        const updatedPrefs = {
          ...currentPrefs,
          defaultLandingPage: DEFAULT_PREFERENCES.defaultLandingPage
        };
        await setDoc(doc(db, 'userPreferences', userId), updatedPrefs);
        return updatedPrefs;
      }

      return currentPrefs;
    } catch (error) {
      // console.error supprimé
      return DEFAULT_PREFERENCES;
    }
  },

  async updatePositionCalculatorPreferences(preferences: Partial<PositionCalculatorPreferences>): Promise<void> {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const userPreferencesRef = doc(db, 'userPreferences', userId);
      await updateDoc(userPreferencesRef, {
        positionCalculator: {
          ...(await this.getUserPreferences()).positionCalculator,
          ...preferences
        }
      });
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  },

  async updateTradeFormPreferences(preferences: Partial<TradeFormPreferences>): Promise<void> {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const userPreferencesRef = doc(db, 'userPreferences', userId);
      const currentPrefs = await this.getUserPreferences();
      
      const updatedPrefs = {
        ...currentPrefs,
        tradeForm: {
          ...currentPrefs.tradeForm,
          ...preferences
        }
      };

      await setDoc(userPreferencesRef, updatedPrefs);
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  },

  async updateRetailPreferences(preferences: Partial<RetailPreferences>): Promise<void> {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const userPreferencesRef = doc(db, 'userPreferences', userId);
      await updateDoc(userPreferencesRef, {
        retail: {
          ...(await this.getUserPreferences()).retail,
          ...preferences
        }
      });
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  },

  async updateSeasonalityPreferences(preferences: Partial<SeasonalityPreferences>): Promise<void> {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const userPreferencesRef = doc(db, 'userPreferences', userId);
      await updateDoc(userPreferencesRef, {
        seasonality: {
          ...(await this.getUserPreferences()).seasonality,
          ...preferences
        }
      });
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  },

  async updateMarketClockPreferences(preferences: Partial<MarketClockPreferences>): Promise<void> {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const userPrefsRef = doc(db, 'userPreferences', userId);
    const currentPrefs = await getDoc(userPrefsRef);

    if (!currentPrefs.exists()) {
      await setDoc(userPrefsRef, {
        marketClock: preferences
      });
    } else {
      await updateDoc(userPrefsRef, {
        'marketClock': preferences
      });
    }
  },

  async updateCalendarPreferences(preferences: Partial<CalendarPreferences>): Promise<void> {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const userPreferencesRef = doc(db, 'userPreferences', userId);
      await updateDoc(userPreferencesRef, {
        calendar: {
          ...(await this.getUserPreferences()).calendar,
          ...preferences
        }
      });
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  },

  async updateIAAnalysisPreferences(preferences: Partial<IAAnalysisPreferences>): Promise<void> {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const userPreferencesRef = doc(db, 'userPreferences', userId);
      await updateDoc(userPreferencesRef, {
        iaAnalysis: {
          ...(await this.getUserPreferences()).iaAnalysis,
          ...preferences
        }
      });
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  },

  async updatePropFirmSimulatorDisclaimerPreferences(preferences: Partial<PropFirmSimulatorDisclaimerPreferences>): Promise<void> {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;

    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const userPreferencesRef = doc(db, 'userPreferences', userId);
      const currentPrefs = await this.getUserPreferences();

      await setDoc(userPreferencesRef, {
        ...currentPrefs,
        propFirmSimulatorDisclaimer: {
          ...currentPrefs.propFirmSimulatorDisclaimer,
          ...preferences
        }
      });
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  },

  async updateUserPreferences(preferences: Partial<UserPreferences>): Promise<void> {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const userPreferencesRef = doc(db, 'userPreferences', userId);
      const currentPrefs = await this.getUserPreferences();
      
      await updateDoc(userPreferencesRef, {
        ...currentPrefs,
        ...preferences
      });
    } catch (error) {
      // console.error supprimé
      throw error;
    }
  },
};
